<template lang="pug">
    .email-card(:class="{ 'is-outbound': isOutbound, 'is-collapsed': !expanded, 'is-unread': message.direction === 'inbound' && !message.is_read }")
        .email-card__head(@click="expanded = !expanded")
            span.email-card__dir(:class="isOutbound ? 'outbound' : 'inbound'" :title="isOutbound ? $t('Outgoing') : $t('Incoming')")
                i(:class="isOutbound ? 'el-icon-fa-arrow-up fas' : 'el-icon-fa-arrow-down fas'")
                //- Unread marker: the same red dot language as the conversation list,
                //- on the always-visible chip (works collapsed or expanded).
                span.email-card__unread-dot(v-if="message.direction === 'inbound' && !message.is_read" :title="$t('Unread')")
            .email-card__lines
                .email-card__top
                    span.email-card__actor {{ actorLabel }}
                    span.email-card__origin(
                        v-if="senderAttribution && senderAttribution.origin"
                        :class="'is-' + senderAttribution.origin.kind"
                    )
                        i(:class="senderAttribution.origin.kind === 'pos' ? 'el-icon-fa-store fas' : 'el-icon-fa-building fas'")
                        | {{ senderAttribution.origin.name }}
                    .email-card__meta
                        el-tooltip(v-if="statusMeta && agentActions" :content="statusMeta.label" placement="top")
                            i.email-card__status-icon(:class="[statusMeta.icon, { 'is-error': statusMeta.isError }]")
                        span.email-card__actions(v-if="expanded && agentActions")
                            //- Per-message read/unread — only inbound mail carries an unread
                            //- state (outbound copies + notes are always read).
                            el-tooltip(v-if="allowReadState && message.direction === 'inbound'" :content="message.is_read ? $t('Mark as unread') : $t('Mark as read')" placement="top")
                                i.far(
                                    :class="message.is_read ? 'el-icon-fa-envelope' : 'el-icon-fa-envelope-open'"
                                    @click.stop="$emit('mark-message-read', { id: message.id, read: !message.is_read })"
                                )
                            el-tooltip(v-if="allowResend && isOutbound" :content="$t('Resend')" placement="top")
                                i.el-icon-fa-rotate-right.fas(@click.stop="$emit('resend', message)")
                            el-tooltip(v-if="allowForward" :content="$t('Forward')" placement="top")
                                i.el-icon-fa-share.fas(@click.stop="$emit('forward', message)")
                        //- Date + chevron are the fixed right anchor so every timestamp
                        //- lines up down the list; the variable status/action icons float
                        //- to the LEFT of the date instead of shoving it around.
                        span.email-card__time {{ formatTime(message.created_at) }}
                        i.email-card__chevron(:class="expanded ? 'el-icon-fa-chevron-up fas' : 'el-icon-fa-chevron-down fas'")
                //- The subject is what tells one message in a thread from another. Without it every
                //- row reads as the mailbox address and a timestamp, so an order confirmation and
                //- the event ticket that followed it are indistinguishable until opened.
                span.email-card__subject(v-if="message.subject") {{ message.subject }}
                span.email-card__snippet(v-if="!expanded") {{ snippet }}
        .email-card__body(v-show="expanded")
            .email-card__recipients(v-if="message.cc_address || message.bcc_address")
                .email-card__rcpt(v-if="message.cc_address")
                    span.email-card__rcpt-label {{ $t('Cc') }}
                    span {{ message.cc_address }}
                .email-card__rcpt(v-if="message.bcc_address")
                    span.email-card__rcpt-label {{ $t('Bcc') }}
                    span {{ message.bcc_address }}
            .email-card__engagement(v-if="isOutbound && agentActions")
                engagement-strip(
                    :sent-email="message.sentEmail"
                    :fallback-sent-at="message.sent_at"
                    :status="message.status"
                    :date-time-format="dateTimeFormat"
                )
            iframe.email-card__frame(
                v-if="hasBody"
                ref="frame"
                sandbox="allow-same-origin"
                :srcdoc="srcDoc"
                @load="applyQuoteVisibility"
            )
            .email-card__empty(v-else)
                i.el-icon-fa-envelope-open.far
                span {{ $t('No content available') }}
            .email-card__quoted(v-if="hasQuoted")
                span.email-card__quoted-toggle(@click="showQuoted = !showQuoted")
                    i(:class="showQuoted ? 'el-icon-fa-chevron-up fas' : 'el-icon-fa-ellipsis-h fas'")
                    | {{ showQuoted ? $t('Hide quoted history') : $t('Show quoted history') }}
            .email-card__attachments(v-if="visibleAttachments.length")
                a.attachment(
                    v-for="file in visibleAttachments"
                    :key="file.id"
                    :href="file.signed_url || file.url"
                    target="_blank"
                    rel="noopener"
                )
                    i.el-icon-fa-paperclip.fas
                    | {{ file.name }}
                    span.attachment__size(v-if="file.size") ({{ humanSize(file.size) }})
            //- End-of-message marker. Seeing THIS is what counts as having read
            //- the mail (see readObserver) — a marker rather than the card
            //- itself, so a mail taller than the window still resolves.
            .email-card__read-marker(ref="readMarker")
</template>

<i18n>
{
    "de": {
        "Outgoing": "Ausgang",
        "Incoming": "Eingang",
        "No content available": "Kein Inhalt verfügbar",
        "yyyy-MM-dd": "dd.MM.yyyy",
        "To": "An",
        "Cc": "Cc",
        "Bcc": "Bcc",
        "Show quoted history": "Zitierten Verlauf anzeigen",
        "Hide quoted history": "Zitierten Verlauf ausblenden",
        "Forward": "Weiterleiten",
        "Resend": "Erneut senden",
        "Mark as read": "Als gelesen markieren",
        "Mark as unread": "Als ungelesen markieren",
        "Unread": "Ungelesen",
        "status.draft": "Entwurf",
        "status.queued": "In Warteschlange",
        "status.sent": "Gesendet",
        "status.delivered": "Zugestellt",
        "status.opened": "Geöffnet",
        "status.clicked": "Angeklickt",
        "status.bounced": "Unzustellbar",
        "status.read": "Gelesen",
        "status.failed": "Fehlgeschlagen",
        "status.received": "Empfangen",
        "yyyy-MM-dd": "dd.MM.yyyy"
    },
    "en": {
        "status.draft": "Draft",
        "status.queued": "Queued",
        "status.sent": "Sent",
        "status.delivered": "Delivered",
        "status.opened": "Opened",
        "status.clicked": "Clicked",
        "status.bounced": "Bounced",
        "status.read": "Read",
        "status.failed": "Failed",
        "status.received": "Received"
    }
}
</i18n>

<script>
import _ from 'lodash'
import { timestamp2date } from '@/filters'
import { useSetupStore } from '@/stores'
import EngagementStrip from '@/extensions/shop/views/Purchase/Form/Partials/EngagementStrip'

export default {
    name: 'EmailMessageCard',
    components: {
        EngagementStrip
    },
    props: {
        message: {
            type: Object,
            required: true
        },
        defaultExpanded: {
            type: Boolean,
            default: false
        },
        // Forwarding needs a compose surface to forward INTO. A host without one
        // (the ticket thread) turns the action off rather than offering a dead
        // button.
        allowForward: {
            type: Boolean,
            default: true
        },
        // Off where the reader is not the agent (the CMS admins' window onto our
        // desk, PlatformTicketsDrawer): no read state, resend, delivery status or
        // engagement, only the mail itself.
        agentActions: {
            type: Boolean,
            default: true
        },
        // Read state and resend change the message, which needs the update
        // right on whatever owns the thread. Without it the mail stays
        // readable and its unread state is left alone.
        allowReadState: {
            type: Boolean,
            default: true
        },
        allowResend: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            expanded: this.defaultExpanded,
            showQuoted: false,
            // Non-reactive by intent: the observer is plumbing, not state.
            readObserver: null
        }
    },
    computed: {
        isOutbound() {
            return this.message.direction === 'outbound'
        },
        dateTimeFormat() {
            return this.$t('yyyy-MM-dd') + ', HH:mm'
        },
        senderLabel() {
            return this.isOutbound
                ? (this.message.to_address || '')
                : (this.message.from_address || '')
        },
        // The primary identity on the collapsed card: WHO acted. Outbound → the agent
        // who sent it (falling back to the sending address for system emails); inbound
        // → the correspondent who wrote. The recipient/counterpart shows on expand.
        actorLabel() {
            if (this.isOutbound) {
                const person = _.filter([
                    _.get(this.message, 'user.first_name'),
                    _.get(this.message, 'user.last_name')
                ]).join(' ')
                return person || this.message.from_address || this.message.to_address || ''
            }
            return this.message.from_address || ''
        },
        // Who actually sent an outbound message: the person, plus — when it adds
        // information — where it came from. A POS shows as a neutral chip; a DIFFERENT
        // CMS on a shared mailbox shows as an accented chip (a reply another tenant
        // sent). Same-CMS/no-POS just shows the person, quietly.
        senderAttribution() {
            if (!this.isOutbound) {
                return null
            }
            const message = this.message
            const person = _.filter([
                _.get(message, 'user.first_name'),
                _.get(message, 'user.last_name')
            ]).join(' ')
            const posName = _.get(message, 'originPointOfSale.name')
            const originCmsId = _.get(message, 'originCms.id')
            const activeCmsId = _.get(useSetupStore().activeCms, 'id')
            const isForeignCms = originCmsId && activeCmsId && String(originCmsId) !== String(activeCmsId)

            let origin = null
            if (posName) {
                origin = { name: posName, kind: 'pos' }
            } else if (isForeignCms) {
                origin = { name: _.get(message, 'originCms.name'), kind: 'cms' }
            }

            if (!person && !origin) {
                return null
            }
            return { person, origin }
        },
        hasQuoted() {
            // A stripped visible_body means there is quoted history to reveal.
            // The letter as sent is shown whole: nothing is folded away.
            if (this.message.sent_html) return false
            return !!this.message.visible_body && this.message.visible_body !== this.message.body
        },
        // Whether there's any renderable body. System emails store their HTML on the
        // mail-tracker disk; if that file can't be read (e.g. pruned, or only present
        // in another environment) the mirrored body is empty — show a placeholder
        // instead of a blank frame.
        hasBody() {
            return !!(this.message.body || this.message.visible_body || this.message.body_plain)
        },
        // Outbound emails arrive as the full tenant-styled document (own <head>/CSS,
        // e.g. logo header + footer) — render it verbatim so the thread matches the
        // email the recipient received. Inbound mails keep the trimmed, quote-stripped
        // frameDoc (with the quoted-history toggle).
        srcDoc() {
            // A requester's window onto their thread (the CMS's drawer onto our
            // desk) flips our mail to "incoming" and hands the letter they got as
            // sent_html: shown whole, like our own outbound mail here.
            const letter = this.message.sent_html || (this.isOutbound ? this.message.body : '')
            if (letter && /<!doctype|<html/i.test(letter)) {
                // Strip our own open-pixel / click-tracking before rendering, so merely
                // viewing the thread can never register a false "opened"/click on the
                // mail-tracker record (the head/styles are preserved).
                return this.stripTrackingDocument(letter)
            }
            return this.frameDoc
        },
        // The message.status only ever advances to 'sent' — the real lifecycle
        // (delivered/opened/clicked/bounced) lives on the linked mail-tracker record.
        // Surface the furthest state reached so the header agrees with the engagement
        // strip instead of forever reading "Gesendet".
        headerStatusLabel() {
            const sentEmail = this.message.sentEmail
            if (sentEmail) {
                if (sentEmail.clicked_at) return this.$t('status.clicked')
                if (sentEmail.opened_at) return this.$t('status.opened')
                if (sentEmail.bounced_permanent_at || sentEmail.bounced_temporary_at) return this.$t('status.bounced')
                if (sentEmail.delivered_at) return this.$t('status.delivered')
            }
            return this.messageStatusLabel(this.message.status)
        },
        // A failed send or a permanent/temporary bounce → surface the status in red.
        isFailed() {
            const sentEmail = this.message.sentEmail
            return this.message.status === 'failed'
                || !!(sentEmail && (sentEmail.bounced_permanent_at || sentEmail.bounced_temporary_at))
        },
        // Outbound status as a single icon (label via tooltip) — replaces the wordy
        // "• Opened" text so the right cluster stays quiet. Furthest state wins.
        statusMeta() {
            if (!this.isOutbound) {
                return null
            }
            const sentEmail = this.message.sentEmail
            let icon = 'el-icon-fa-paper-plane fas'
            if (this.isFailed) {
                icon = 'el-icon-fa-triangle-exclamation fas'
            } else if (this.message.status === 'queued') {
                icon = 'el-icon-fa-clock far'
            } else if (sentEmail && sentEmail.clicked_at) {
                icon = 'el-icon-fa-arrow-pointer fas'
            } else if (sentEmail && sentEmail.opened_at) {
                icon = 'el-icon-fa-envelope-open fas'
            } else if (sentEmail && sentEmail.delivered_at) {
                icon = 'el-icon-fa-circle-check fas'
            }
            // On failure, append WHY it failed (the driver's captured reason) so the agent
            // sees "Fehlgeschlagen: 550 recipient rejected" instead of a bare "Fehlgeschlagen".
            const reason = this.isFailed ? _.get(this.message, 'status_reason') : ''
            const label = reason ? `${this.headerStatusLabel}: ${reason}` : this.headerStatusLabel
            return { icon, label, isError: this.isFailed }
        },
        snippet() {
            // Strip from HTML so block boundaries (</p>, <br>, …) become spaces —
            // body_plain is flattened and runs words together ("testMit freundlichen").
            // Use the chrome-free source: quote_body (the composed message WITHOUT the
            // tenant layout header/footer) for outbound, the quote-stripped visible_body
            // for inbound. Fall back to body_plain (mirrored system emails keep only it).
            const html = this.isOutbound
                ? (this.message.quote_body || this.message.visible_body)
                : (this.message.visible_body || this.message.body)
            const stripped = html ? this.stripTags(html) : (this.message.body_plain || '')
            const source = this.decodeEntities(stripped)
            return _.truncate(source.replace(/\s+/g, ' ').trim(), { length: 90 })
        },
        // Attachments shown as downloadable chips — minus the inline images already
        // rendered in the body via cid: (e.g. signature logos), which shouldn't be
        // listed twice.
        visibleAttachments() {
            const attachments = this.message.attachments || []
            const inlineIds = this.inlineAttachmentIds
            return attachments.filter(file => !inlineIds.has(file.id))
        },
        // Ids of attachments referenced inline (cid:) anywhere in the body.
        inlineAttachmentIds() {
            const ids = new Set()
            const body = String(this.message.body || '')
            const regex = /cid:([^"')\s]+)/gi
            let match
            while ((match = regex.exec(body)) !== null) {
                const file = this.matchAttachmentByCid(match[1])
                if (file) ids.add(file.id)
            }
            return ids
        },
        // The iframe document holds BOTH the trimmed body and the full (quoted) body.
        // It deliberately does NOT depend on showQuoted: toggling the quote flips the
        // display of these blocks inside the already-loaded document (see
        // applyQuoteVisibility) rather than re-binding srcdoc, which would reload the
        // iframe and flicker. No allow-scripts + stripped <script> keep email JS inert;
        // base target=_blank opens links in a new tab.
        frameDoc() {
            const full = this.message.body || this.message.body_plain || ''
            const visible = this.trimTrailingEmpty(this.message.visible_body || full)
            // Resolve inline cid: images to their stored attachment URLs, then strip
            // our tracking artifacts.
            const strip = html => this.stripTracking(this.resolveCidImages(html))
            const blocks = this.hasQuoted
                ? '<div id="visible-body">' + strip(visible) + '</div>'
                    + '<div id="full-body" style="display:none">' + strip(full) + '</div>'
                : '<div id="visible-body">' + strip(visible) + '</div>'
            return '<!doctype html><html><head><meta charset="utf-8">'
                // Send no referrer so hotlink-protected CDNs still serve images.
                // (Expired signed URLs — e.g. old Instagram/Amazon links — can't be
                // recovered and will still show as broken, same as any mail client.)
                + '<meta name="referrer" content="no-referrer">'
                + '<base target="_blank">'
                + '<style>'
                + 'html,body{margin:0;padding:0}'
                + 'body{font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;font-size:13px;line-height:1.5;color:#303133;padding:10px 12px;word-break:break-word}'
                + 'img{max-width:100%;height:auto}'
                + 'a{color:#409EFF}'
                + 'blockquote{margin:8px 0;padding-left:10px;border-left:2px solid #dcdfe6;color:#909399}'
                + '</style></head><body>' + blocks + '</body></html>'
        }
    },
    mounted() {
        this.observeRead()
    },
    beforeDestroy() {
        if (this.readObserver) this.readObserver.disconnect()
    },
    watch: {
        // Toggle the quote in-place — no srcdoc rebind, so the iframe never reloads.
        showQuoted() {
            this.applyQuoteVisibility()
        },
        expanded(value) {
            if (value) this.$nextTick(() => this.resizeFrame())
        }
    },
    methods: {
        // Reading a mail is EXPANDED plus SCROLLED PAST, the way Apple Mail
        // handles a stacked conversation — not merely "the thread was opened",
        // which would burn through a long thread's unread state unseen. The
        // marker sits at the END of the body, so a mail taller than the window
        // counts only once you have actually reached its bottom; collapsed, the
        // body is display:none and never intersects at all.
        //
        // Per MESSAGE, deliberately: a thread with three unread mails where one
        // is read must keep the other two unread.
        observeRead() {
            if (!this.agentActions || !this.allowReadState || this.message.direction !== 'inbound' || this.message.is_read) return
            if (typeof IntersectionObserver === 'undefined') return

            const marker = this.$refs.readMarker
            if (!marker) return

            this.readObserver = new IntersectionObserver(entries => {
                if (!entries.some(entry => entry.isIntersecting)) return

                this.readObserver.disconnect()
                this.readObserver = null
                this.$emit('mark-message-read', { id: this.message.id, read: true })
            })
            this.readObserver.observe(marker)
        },
        // Show the trimmed or the full (quoted) body inside the loaded iframe, then
        // resize. Runs on @load and whenever showQuoted flips — never reloads the frame.
        applyQuoteVisibility() {
            const frame = this.$refs.frame
            if (!frame) return
            try {
                const doc = frame.contentDocument || (frame.contentWindow && frame.contentWindow.document)
                if (doc) {
                    const visibleBody = doc.getElementById('visible-body')
                    const fullBody = doc.getElementById('full-body')
                    if (visibleBody && fullBody) {
                        visibleBody.style.display = this.showQuoted ? 'none' : ''
                        fullBody.style.display = this.showQuoted ? '' : 'none'
                    }
                }
            } catch (error) {
                // cross-origin guard
            }
            this.resizeFrame()
        },
        resizeFrame() {
            const frame = this.$refs.frame
            if (!frame) return
            try {
                const doc = frame.contentDocument || (frame.contentWindow && frame.contentWindow.document)
                if (doc && doc.body) {
                    // body.scrollHeight reports the true content height and (unlike
                    // documentElement.scrollHeight) does NOT inflate to the iframe
                    // viewport once a height is set — so no collapse-to-0 is needed,
                    // which is what made toggling flicker.
                    frame.style.height = (doc.body.scrollHeight + 4) + 'px'
                    return
                }
            } catch (error) {
                // cross-origin guard — fall back to a sensible default height
            }
            frame.style.height = '120px'
        },
        stripTags(html) {
            // Drop style/script blocks whole (their contents aren't text), then turn
            // every remaining tag into a space so words never fuse across elements.
            return String(html)
                .replace(/<(style|script)[^>]*>[\s\S]*?<\/\1>/gi, ' ')
                .replace(/<[^>]*>/g, ' ')
        },
        // Turn HTML entities (&amp;, &nbsp;, &#160;, …) back into their characters so a
        // preview never shows raw "&amp;". Textarea decoding handles the full set.
        decodeEntities(text) {
            const element = document.createElement('textarea')
            element.innerHTML = String(text)
            return element.value
        },
        // Find the stored attachment a cid: reference points at. Inbound attachments
        // are stored as "<hash>-<original-filename>", and the Content-ID equals the
        // original filename, so the cid value is the tail of the stored name.
        matchAttachmentByCid(cid) {
            const value = String(cid).trim()
            if (!value) return null
            const attachments = this.message.attachments || []
            return attachments.find(file => {
                const name = file.name || ''
                return name === value || name.endsWith('-' + value) || name.endsWith(value)
            }) || null
        },
        // Rewrite inline cid: image references to the matching stored attachment URL.
        resolveCidImages(html) {
            if (!/cid:/i.test(html)) return html
            return String(html).replace(/(["'])cid:([^"']+)\1/gi, (whole, quote, cid) => {
                const file = this.matchAttachmentByCid(cid)
                const url = file && (file.signed_url || file.url)
                return url ? `${quote}${url}${quote}` : whole
            })
        },
        // Remove our OWN mail-tracker artifacts before the body enters the iframe so
        // the webmailer never self-counts opens/clicks. An inbound reply quotes the
        // original sent email, which carries the open pixel (…/t/<hash>) and click
        // links (…/l/<hash>/<url>); rendering those would hit the tracker on our end.
        stripTracking(html) {
            let parsed
            try {
                parsed = new DOMParser().parseFromString(String(html), 'text/html')
            } catch (error) {
                return String(html).replace(/<script[\s\S]*?<\/script>/gi, '')
            }
            parsed.querySelectorAll('script').forEach(node => node.remove())
            // Open-tracking pixels.
            parsed.querySelectorAll('img').forEach(img => {
                const src = img.getAttribute('src') || ''
                if (/\/t\/[A-Za-z0-9_-]{12,}/.test(src)) img.remove()
            })
            // Click-tracking redirects → point at the real target (or neutralise).
            parsed.querySelectorAll('a[href]').forEach(link => {
                const href = link.getAttribute('href') || ''
                const match = href.match(/\/l\/[A-Za-z0-9_-]{12,}\/(.+)$/)
                if (!match) return
                let target = '#'
                try {
                    const decoded = decodeURIComponent(match[1])
                    if (/^https?:\/\//i.test(decoded)) target = decoded
                } catch (error) { /* leave neutralised */ }
                link.setAttribute('href', target)
            })
            return parsed.body ? parsed.body.innerHTML : String(html)
        },
        // Strip our open-pixel / click-tracking from a FULL email document while
        // keeping its <head>/styles intact (unlike stripTracking, which returns only
        // body innerHTML). Used for the outbound full-document render so viewing the
        // thread never self-registers an open/click.
        stripTrackingDocument(html) {
            let doc
            try {
                doc = new DOMParser().parseFromString(String(html), 'text/html')
            } catch (error) {
                return String(html)
            }
            doc.querySelectorAll('script').forEach(node => node.remove())
            doc.querySelectorAll('img').forEach(img => {
                const src = img.getAttribute('src') || ''
                if (/\/t\/[A-Za-z0-9_-]{12,}/.test(src)) img.remove()
            })
            doc.querySelectorAll('a[href]').forEach(link => {
                const href = link.getAttribute('href') || ''
                const match = href.match(/\/l\/[A-Za-z0-9_-]{12,}\/(.+)$/)
                if (!match) return
                let target = '#'
                try {
                    const decoded = decodeURIComponent(match[1])
                    if (/^https?:\/\//i.test(decoded)) target = decoded
                } catch (error) { /* leave neutralised */ }
                link.setAttribute('href', target)
            })
            return '<!doctype html>' + doc.documentElement.outerHTML
        },
        // Drop trailing whitespace, <br>s, and empty block elements so a hidden
        // quote (or a body that ends in blank lines) doesn't leave dead space.
        trimTrailingEmpty(html) {
            let out = String(html)
            let prev
            do {
                prev = out
                out = out
                    .replace(/\s+$/, '')
                    .replace(/(?:<br\s*\/?>)+$/i, '')
                    .replace(/<(div|p|span|o:p)\b[^>]*>(?:\s|&nbsp;|&#160;|<br\s*\/?>)*<\/\1>$/i, '')
            } while (out !== prev)
            return out
        },
        humanSize(bytes) {
            if (!bytes) return ''
            const units = ['B', 'KB', 'MB', 'GB']
            let value = bytes
            let unit = 0
            while (value >= 1024 && unit < units.length - 1) {
                value /= 1024
                unit++
            }
            return `${value % 1 === 0 ? value : value.toFixed(1)} ${units[unit]}`
        },
        messageStatusLabel(status) {
            const map = {
                draft: this.$t('status.draft'),
                queued: this.$t('status.queued'),
                sent: this.$t('status.sent'),
                delivered: this.$t('status.delivered'),
                read: this.$t('status.read'),
                failed: this.$t('status.failed'),
                received: this.$t('status.received')
            }
            return map[status] || status
        },
        formatTime(timestamp) {
            if (!timestamp) return ''
            return timestamp2date(timestamp, this.$t('yyyy-MM-dd') + ', HH:mm')
        }
    }
}
</script>

<style lang="sass" scoped>
// One pixel at the very end of the mail: intersecting it means the reader
// reached the bottom of the message (see observeRead).
.email-card__read-marker
    height: 1px

.email-card
    margin-bottom: 10px
    background: #fff
    border: 1px solid #ebeef5
    border-radius: 6px
    // border-left: 2px solid #e4e7ed
    // border-top-left-radius: 0
    // border-bottom-left-radius: 0
    overflow: hidden
    // Direction reads from the colored edge — blue out, green in.
    // &.is-outbound
    //     border-left-color: #409EFF
    // &:not(.is-outbound)
    //     border-left-color: #67c23a
    // Unread inbound message — a bolder actor, cleared when read. The red dot on the
    // direction chip (below) is the primary marker; no colored border.
    &.is-unread .email-card__actor
        font-weight: 600
    &.is-collapsed .email-card__head
        border-bottom: none
    &__head
        display: flex
        align-items: center
        gap: 10px
        padding: 9px 12px
        cursor: pointer
        border-bottom: 1px solid #f2f6fc
        &:hover
            background: #fafcff
    // The direction chip lives in the left gutter, vertically centered against BOTH
    // lines (the snippet is indented under it), so it reads as the row's marker.
    &__dir
        position: relative
        flex-shrink: 0
        width: 20px
        height: 20px
        display: inline-flex
        align-items: center
        justify-content: center
        border-radius: 50%
        font-size: .62rem
        &.inbound
            color: #67c23a
            background: #f0f9eb
        &.outbound
            color: #409EFF
            background: #ecf5ff
    // Unread marker — same red dot + white ring as the conversation-list rows.
    &__unread-dot
        position: absolute
        top: -2px
        right: -2px
        width: 9px
        height: 9px
        border-radius: 50%
        background: #f56c6c
        box-shadow: 0 0 0 1.5px #fff
    // The two stacked lines (top row + preview) beside the gutter chip.
    &__lines
        flex: 1
        min-width: 0
        display: flex
        flex-direction: column
        gap: 3px
    // Top row: actor, origin and the right-hand meta share one vertical center.
    &__top
        display: flex
        align-items: center
        gap: 8px
        min-width: 0
    &__actor
        flex-shrink: 1
        min-width: 0
        font-weight: 600
        font-size: .85rem
        color: #303133
        overflow: hidden
        text-overflow: ellipsis
        white-space: nowrap
    &__origin
        flex-shrink: 0
        display: inline-flex
        align-items: center
        gap: 4px
        max-width: 260px
        padding: 1px 8px
        border-radius: 10px
        font-size: .68rem
        font-weight: 500
        white-space: nowrap
        overflow: hidden
        text-overflow: ellipsis
        background: #f0f2f5
        color: #606266
        i
            font-size: .62rem
            opacity: .75
        &.is-cms
            background: rgba(64, 158, 255, .12)
            color: #409EFF
    // Right-hand cluster: pushed to the edge, everything vertically centered. Actions
    // only exist when the card is open (rendered, not hidden — no empty placeholders).
    &__meta
        flex-shrink: 0
        margin-left: auto
        padding-left: 10px
        display: inline-flex
        align-items: center
        gap: 12px
        font-size: .72rem
        color: #909399
        .email-card__time
            white-space: nowrap
        .email-card__status-icon
            display: inline-flex
            align-items: center
            font-size: .72rem
            line-height: 1
            // Box-centered already (measured); this nudges the glyph's ink, which sits
            // high in its em box, down to optically match the digits.
            transform: translateY(-4%)
            color: #b4b7bd
            &.is-error
                color: #f56c6c
        .email-card__actions
            display: inline-flex
            align-items: center
            gap: 12px
            i
                cursor: pointer
                color: #a8abb3
                &:hover
                    color: #409EFF
        .email-card__chevron
            color: #c0c4cc
    // Preview on its own row; the lines column already sits past the gutter chip.
    &__subject
        font-weight: 600
        font-size: .82rem
        color: #303133
        overflow: hidden
        text-overflow: ellipsis
        white-space: nowrap
    &__snippet
        color: #909399
        font-size: .8rem
        overflow: hidden
        text-overflow: ellipsis
        white-space: nowrap
    &__body
        padding: 0
    &__engagement
        padding: 8px 12px
        border-bottom: 1px solid #f2f6fc
    &__recipients
        padding: 8px 12px
        border-bottom: 1px solid #f2f6fc
        font-size: .76rem
        color: #606266
    &__rcpt
        display: flex
        gap: 6px
        line-height: 1.5
        word-break: break-word
    &__rcpt-label
        flex: 0 0 auto
        min-width: 28px
        color: #909399
    &__frame
        width: 100%
        border: 0
        display: block
        min-height: 60px
    &__empty
        display: flex
        align-items: center
        gap: 8px
        padding: 16px 14px
        color: #909399
        font-size: .8rem
        font-style: italic
        i
            font-size: .95rem
            color: #c0c4cc
    &__quoted
        padding: 6px 12px
        border-top: 1px solid #f2f6fc
        &-toggle
            cursor: pointer
            font-size: .75rem
            color: #409EFF
            i
                margin-right: 5px
    &__attachments
        padding: 8px 12px
        border-top: 1px solid #f2f6fc
        font-size: .78rem
        display: flex
        flex-wrap: wrap
        gap: 8px
        .attachment
            display: inline-flex
            align-items: center
            padding: 3px 10px
            border: 1px solid #ebeef5
            border-radius: 12px
            color: #409EFF
            text-decoration: none
            background: #f5f7fa
            &:hover
                background: #ecf5ff
            i
                margin-right: 5px
                color: #909399
            .attachment__size
                margin-left: 5px
                color: #909399
</style>
